import { ref, watch } from 'vue'

/**
 * Composable for managing dark mode functionality.
 *
 * SINGLETON: `isDark` and all init/apply logic live at module scope, so every
 * consumer (desktop Navbar, mobile header toggle, mobile More page) shares one
 * reactive source. This mirrors the pattern in `useChartTheme.js` and prevents
 * the desync where two controls bound to separate per-instance refs go stale
 * when one of them toggles the `.dark` class.
 *
 * Stores preference in localStorage and applies to the document root.
 */

// Single shared reactive source for the whole app.
const isDark = ref(false)
let initialized = false

// Apply theme to the document root.
function applyTheme() {
  document.documentElement.classList.toggle('dark', isDark.value)
}

// Initialize once: read saved/system preference, apply it, and start listening
// for system theme changes. Guarded so repeated imports/consumers are no-ops.
function initDarkMode() {
  if (initialized) return
  initialized = true

  const savedTheme = localStorage.getItem('theme')
  if (savedTheme) {
    isDark.value = savedTheme === 'dark'
  } else {
    // Fall back to system preference.
    isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches
  }
  applyTheme()

  // System theme listener — registered once at module scope, lives for the app
  // lifetime (no per-component teardown, so no consumer can orphan the others).
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.addEventListener('change', (e) => {
    // Only follow the system if the user hasn't set an explicit preference.
    if (!localStorage.getItem('theme')) {
      isDark.value = e.matches
    }
  })
}

// Persist + re-apply on every change. Registered once (module eval is once per
// ESM import).
watch(isDark, (newValue) => {
  localStorage.setItem('theme', newValue ? 'dark' : 'light')
  applyTheme()
})

// Run init at module load so the theme is correct before any component mounts.
initDarkMode()

function toggleDarkMode() {
  isDark.value = !isDark.value
}

export default function useDarkMode() {
  return {
    isDark,
    toggleDarkMode,
  }
}
